import { useEffect, useRef, useCallback, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace(/^http/, 'ws') + '/api/ws/chat';

export function useChat() {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const handlersRef = useRef({});

  const on = useCallback((event, handler) => {
    handlersRef.current[event] = handler;
  }, []);

  const emit = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) return;

    // Token sent via cookie; pass in URL as fallback for WS upgrade
    const token = sessionStorage.getItem('token') || '';
    const ws = new WebSocket(token ? `${WS_URL}?token=${token}` : WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        const handler = handlersRef.current[data.type];
        if (handler) handler(data);

        if (data.type === 'online_users') {
          setOnlineUsers(new Set(data.users));
        } else if (data.type === 'presence') {
          setOnlineUsers(prev => {
            const next = new Set(prev);
            data.online ? next.add(data.user_id) : next.delete(data.user_id);
            return next;
          });
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      // Auto-reconnect after 3 seconds
      reconnectTimer.current = setTimeout(() => connect(), 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    wsRef.current?.close();
    wsRef.current = null;
    setConnected(false);
  }, []);

  useEffect(() => {
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, []);

  return { connect, disconnect, connected, onlineUsers, on, emit };
}
